/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import type { ColorSchemePreference } from '@coursera-web/appearance';
import {
  getColorSchemePreference,
  setColorSchemePreference,
  supportsDarkMode,
  useColorTheme,
} from '@coursera-web/appearance';

import { Grid, InlineNotification, SelectField, SelectOption, Typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import _t from 'i18n!nls/account-settings';

const styles = {
  description: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
};

const isColorSchemePreference = (value: unknown): value is ColorSchemePreference =>
  value === 'light' || value === 'dark' || value === 'system';

const AppearancePreferences = () => {
  const [value, setValue] = React.useState<ColorSchemePreference>('light');
  const { onColorThemeChange } = useColorTheme();
  const [isDarkModeSupported, setIsDarkModeSupported] = React.useState<boolean>(false);
  const track = useTracker();

  const sectionRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_page_section', {
    pageSection: {
      sectionName: 'appearance_settings',
    },
  });

  // get default value based on existing preference
  useEffect(() => {
    const existingPreference = getColorSchemePreference();
    setValue(existingPreference || 'light');
    setIsDarkModeSupported(supportsDarkMode());
  }, []);

  const handleModeSelection = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newValue = event.target.value;
    if (!isColorSchemePreference(newValue)) return;
    track('change_select', {
      select: {
        name: 'color_theme',
        value: [newValue],
        label: 'Color theme',
      },
    });
    setValue(newValue);
    setColorSchemePreference(newValue);
    onColorThemeChange(newValue);
  };

  return (
    <div ref={sectionRef}>
      <Grid container spacing={16} alignItems="center">
        <Grid item xs={12} md={8}>
          <Typography2 variant="subtitleLarge" component="h2">
            {_t('Appearance')}
          </Typography2>
          <Typography2 variant="bodySecondary" component="p" css={styles.description}>
            {_t('Personalize the way Coursera appears through theming controls.')}
          </Typography2>
        </Grid>
        {!isDarkModeSupported ? (
          <Grid item xs={12}>
            <InlineNotification severity="information">
              {_t('Dark mode is not supported in this browser. Please update your browser to the latest version.')}
            </InlineNotification>
          </Grid>
        ) : (
          <Grid item xs={12} md={4}>
            <SelectField aria-label={_t('Select color theme')} fullWidth value={value} onChange={handleModeSelection}>
              <SelectOption value="light">{_t('Light mode')}</SelectOption>
              <SelectOption value="dark">{_t('Dark mode')}</SelectOption>
              <SelectOption value="system">{_t('System default')}</SelectOption>
            </SelectField>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default AppearancePreferences;
